import React, { useEffect, useState, useContext } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import { UserContext } from "../../../App";
import "./StudentCoursesPage.css";
import { Link } from "react-router-dom";
import { FaLock, FaClock, FaChevronDown, FaChevronRight } from "react-icons/fa";

const getAvailabilityStatus = (availability, userId) => {
  if (!availability) return "available";

  const now = new Date();

  // Check for user-specific overrides first
  if (availability.userOverrides && availability.userOverrides[userId]) {
    const override = availability.userOverrides[userId];
    const startDate = override.startDate ? new Date(override.startDate) : null;
    const endDate = override.endDate ? new Date(override.endDate) : null;

    if ((startDate && now < startDate) || (endDate && now > endDate))
      return "expired";
    return "available";
  }

  // Fall back to general availability
  const startDate = availability.startDate
    ? new Date(availability.startDate)
    : null;
  const endDate = availability.endDate ? new Date(availability.endDate) : null;

  if ((startDate && now < startDate) || (endDate && now > endDate))
    return "expired";
  return "available";
};

const AvailabilityIcon = ({ status }) => {
  if (status === "expired") {
    return <FaLock className="availability-icon" title="Not available" />;
  }
  return null;
};

const MyCourses = () => {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { uid } = useContext(UserContext);
  const [showError, setShowError] = useState(false);
  const [expandedCourses, setExpandedCourses] = useState({}); // Track expanded state

  const handleModuleClick = (e, status) => {
    if (status === "expired") {
      e.preventDefault();
      setShowError(true);
    }
  };

  useEffect(() => {
    const fetchStudentCourses = async () => {
      try {
        const getStudentCoursesFunction = httpsCallable(
          functions,
          "getStudentCourses"
        );
        const result = await getStudentCoursesFunction({ studentId: uid });
        setCourses(result.data.courses);
        // Initialize all courses as collapsed
        const initialExpandState = result.data.courses.reduce((acc, course) => {
          acc[course.id] = false;
          return acc;
        }, {});
        setExpandedCourses(initialExpandState);
      } catch (error) {
        console.error("Error fetching student courses:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (uid) {
      fetchStudentCourses();
    }
  }, [uid]);

  const toggleCourse = (courseId) => {
    setExpandedCourses((prev) => ({
      ...prev,
      [courseId]: !prev[courseId],
    }));
  };

  if (showError) {
    return (
      <div className="loading-message module-unavailable">
        <FaClock className="unavailable-icon" />
        <div className="unavailable-text">
          <p>This module is not open yet, please contact your teacher.</p>
          <p className="settings-hint">
            Teachers can adjust this in the course availability settings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-courses-page main-content">
      {isLoading ? (
        <div className="loading">Loading your courses...</div>
      ) : courses.length > 0 ? (
        <ul className="course-list">
          {courses.map((course) => (
            <li key={course.id} className="course-item">
              <div
                className="course-header"
                onClick={() => toggleCourse(course.id)}
              >
                {expandedCourses[course.id] ? (
                  <FaChevronDown className="expand-icon" />
                ) : (
                  <FaChevronRight className="expand-icon" />
                )}
                <h2>{course.courseName}</h2>
              </div>
              {expandedCourses[course.id] && (
                <>
                  {course.sections && course.sections.length > 0 ? (
                    <ul className="section-list">
                      {course.sections.map((section) => (
                        <li key={section.id} className="section-item">
                          <h3>{section.name}</h3>
                          {section.modules && section.modules.length > 0 ? (
                            <ul className="module-list">
                              {section.modules.map((module) => {
                                const availabilityStatus =
                                  getAvailabilityStatus(
                                    section.availability,
                                    uid
                                  );
                                return (
                                  <li key={module.id} className="module-item">
                                    <div className="module-link-container">
                                      <Link
                                        to={`/sview/courses/${course.id}/sections/${section.id}/modules/${module.id}/student/${uid}`}
                                        state={{
                                          courseName: course.courseName,
                                        }}
                                        onClick={(e) =>
                                          handleModuleClick(
                                            e,
                                            availabilityStatus
                                          )
                                        }
                                      >
                                        {module.name}
                                      </Link>
                                      <AvailabilityIcon
                                        status={availabilityStatus}
                                      />
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <p>No modules available in this section</p>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No sections available in this course</p>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="no-courses">
          You are not enrolled in any courses at the moment.
        </p>
      )}
    </div>
  );
};

export default MyCourses;
